import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getDefaultRouteForUser } from "@/lib/roleRedirect";
import { useBranding } from "@/contexts/BrandingContext";
import PageMeta from "@/components/feature/PageMeta";

const isProd = import.meta.env.PROD;

export default function LoginPage() {
  const branding = useBranding();
  const { login, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!authLoading && user) {
      navigate(getDefaultRouteForUser(user), { replace: true });
    }
  }, [authLoading, user, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      const stored = localStorage.getItem("ksm_user");
      if (stored) {
        try {
          const u = JSON.parse(stored);
          navigate(getDefaultRouteForUser(u));
        } catch {
          navigate("/dashboard");
        }
      } else {
        navigate("/dashboard");
      }
    } else {
      setError(result.error || "Error al iniciar sesión");
    }
  };

  if (authLoading || user) {
    return (
      <div className="min-h-screen bg-[var(--color-bg-primary)] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-2xl text-[var(--color-text-muted)]"></i>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg-primary)] flex items-center justify-center relative overflow-hidden">
      <PageMeta title={`Acceso | ${branding.site_name}`} path="/login" noIndex />
      <div className="absolute inset-0 opacity-20">
        <img src={branding.hero_login} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="relative z-10 w-full max-w-md px-4">
        <div className="card p-8">
          <div className="text-center mb-8">
            <img src={branding.logo_full} alt="KSM Services" className="h-10 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-[var(--color-text-primary)]">Acceso al Sistema</h1>
            <p className="text-sm text-[var(--color-text-muted)] mt-1">KSM Training Management</p>
          </div>
          {error && <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4 text-red-400 text-sm">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <input type="email" className="input-field" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            <div className="relative">
              <input type={showPassword ? "text" : "password"} className="input-field pr-10" placeholder="Contraseña" value={password} onChange={(e) => setPassword(e.target.value)} required />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] cursor-pointer">
                <i className={`ri-eye${showPassword ? "-off" : ""}-line`}></i>
              </button>
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50">
              {loading ? <i className="ri-loader-4-line animate-spin"></i> : <><i className="ri-login-box-line"></i> Ingresar</>}
            </button>
            <div className="text-center">
              <Link to="/forgot-password" className="text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-accent-oil)] transition-colors">
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
          </form>
          {!isProd && (
            <div className="mt-6 pt-6 border-t border-[var(--color-border)]">
              <p className="text-xs text-[var(--color-text-muted)] text-center mb-3">Credenciales de prueba (solo desarrollo)</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: "Admin", email: "admin@ksm.com", pass: "admin123" },
                  { label: "Instructor", email: "instructor@ksm.com", pass: "instructor123" },
                  { label: "Evaluador", email: "evaluador@ksm.com", pass: "evaluador123" },
                  { label: "Estudiante", email: "estudiante@ksm.com", pass: "estudiante123" },
                ].map((d) => (
                  <button key={d.email} type="button" onClick={() => { setEmail(d.email); setPassword(d.pass); }}
                    className="text-xs text-left bg-[var(--color-bg-tertiary)] rounded-lg px-3 py-2 cursor-pointer hover:bg-[var(--color-bg-card-hover)]">
                    <span className="text-[var(--color-text-accent)] font-medium">{d.label}</span><br />{d.email}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
